import Dots from './Dots.js';
import { LINK, LinkStyles } from './Link.js';
import { contentOf, isStepList, stepParts } from './Transport.js';
import { resolve } from './tokens.js';

/*
 * Message
 *
 * One turn in the conversation.
 *
 * The customer's turns are short and the assistant's are not, which is why they
 * are drawn differently: the customer's sit in a tinted bubble that hugs its
 * text, the assistant's run full width with no container. A troubleshooting
 * list trapped in a chat bubble is harder to read than the same words set as a
 * document, and reading is the whole job here.
 *
 * THE ASSISTANT DOES NOT REPLY IN PROSE. It replies in a fixed JSON schema:
 * a summary, then any number of sections carrying a header, a description, key
 * and value pairs, and video links. Everything below exists to draw that, and a
 * renderer that reads only the summary throws away every step, every
 * specification and every link without leaving a mark on the screen. See
 * contentOf in Transport.js.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const body = tokens => ({
  color: tokens.text,
  fontFamily: tokens.font,
  fontSize: 15,
  lineHeight: 1.6,
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
});

/*
 * `list_items` carries two different things under one shape, and drawing them
 * the same way is what made this read oddly.
 *
 * Sometimes it is a procedure, where `key` is "1", "2", "3" and `value` is the
 * instruction. Sometimes it is a set of facts, where `key` is "Serial number"
 * or "Last seen" and `value` is the reading. A run of "1. do this" lines with
 * no indentation and no numbering reads as a paragraph that happens to contain
 * digits, and a set of readings rendered as a numbered procedure implies an
 * order that is not there.
 *
 * So the list looks at its own keys. All numeric means a procedure and gets
 * numbered markers with the text hung off them. Anything else is a set of
 * labelled facts and gets label and value pairs.
 */
export const Steps = ({
  items,
  tokens
}) => /*#__PURE__*/_jsx("ol", {
  style: {
    listStyle: 'none',
    margin: '6px 0 0',
    padding: 0
  },
  children: items.map((item, index) => {
    let parts = stepParts(item.key);
    return /*#__PURE__*/_jsxs("li", {
      style: {
        alignItems: 'flex-start',
        display: 'flex',
        gap: 12,
        marginBottom: 14
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          alignItems: 'center',
          backgroundColor: tokens.accent,
          borderRadius: 999,
          color: tokens.onAccent,
          display: 'inline-flex',
          flexShrink: 0,
          fontFamily: tokens.font,
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 700,
          height: 22,
          justifyContent: 'center',
          marginTop: 1,
          width: 22
        },
        children: parts?.number || index + 1
      }), /*#__PURE__*/_jsxs("span", {
        style: {
          minWidth: 0
        },
        children: [parts?.label && /*#__PURE__*/_jsx("span", {
          style: {
            color: tokens.text,
            display: 'block',
            fontFamily: tokens.font,
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 3
          },
          children: parts.label
        }), /*#__PURE__*/_jsx("span", {
          style: {
            color: parts?.label ? tokens.subtext : tokens.text,
            display: 'block',
            fontFamily: tokens.font,
            fontSize: 14,
            lineHeight: 1.6
          },
          children: item.value
        })]
      })]
    }, index);
  })
});
const Facts = ({
  items,
  tokens
}) => /*#__PURE__*/_jsx("dl", {
  style: {
    margin: '4px 0 0'
  },
  children: items.map((item, index) => /*#__PURE__*/_jsxs("div", {
    style: {
      borderTop: index === 0 ? 'none' : `1px solid ${tokens.subdued}`,
      padding: index === 0 ? '0 0 9px' : '9px 0'
    },
    children: [/*#__PURE__*/_jsx("dt", {
      style: {
        color: tokens.subtext,
        fontFamily: tokens.font,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.02em',
        marginBottom: 3
      },
      children: item.key
    }), /*#__PURE__*/_jsx("dd", {
      style: {
        color: tokens.text,
        fontFamily: tokens.font,
        fontSize: 14,
        lineHeight: 1.6,
        margin: 0
      },
      children: item.value
    })]
  }, index))
});
const ItemList = ({
  items,
  tokens
}) => {
  if (!items?.length) {
    return null;
  }
  return isStepList(items) ? /*#__PURE__*/_jsx(Steps, {
    items: items,
    tokens: tokens
  }) : /*#__PURE__*/_jsx(Facts, {
    items: items,
    tokens: tokens
  });
};

// Only https. The model is instructed to return nothing else, which is a good
// reason to check rather than a reason to trust.
const VideoLink = ({
  link,
  tokens
}) => {
  if (!/^https:\/\//i.test(link?.url || '')) {
    return null;
  }
  return /*#__PURE__*/_jsx("li", {
    style: {
      marginBottom: 8
    },
    children: /*#__PURE__*/_jsxs("a", {
      className: LINK,
      href: link.url,
      rel: 'noreferrer',
      style: {
        alignItems: 'center',
        color: tokens.accent,
        display: 'inline-flex',
        fontFamily: tokens.font,
        fontSize: 14,
        fontWeight: 600,
        gap: 7
      },
      target: '_blank',
      children: [/*#__PURE__*/_jsx(LinkStyles, {}), /*#__PURE__*/_jsxs("svg", {
        fill: 'none',
        height: 14,
        stroke: tokens.accent,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: 2,
        viewBox: '0 0 24 24',
        width: 14,
        children: [/*#__PURE__*/_jsx("rect", {
          height: 15,
          rx: 3,
          width: 20,
          x: 2,
          y: 4.5
        }), /*#__PURE__*/_jsx("path", {
          d: 'M10 9.5l5 3-5 3z'
        })]
      }), link.title || 'Watch the walkthrough']
    })
  });
};
const Section = ({
  section,
  tokens
}) => /*#__PURE__*/_jsxs("div", {
  style: {
    marginTop: 16
  },
  children: [section.header && /*#__PURE__*/_jsx("span", {
    style: {
      color: tokens.text,
      display: 'block',
      fontFamily: tokens.font,
      fontSize: 15,
      fontWeight: 600,
      marginBottom: section.sub_header ? 4 : 8
    },
    children: section.header
  }), section.sub_header && /*#__PURE__*/_jsx("p", {
    style: {
      ...body(tokens),
      color: tokens.subtext,
      fontSize: 14,
      marginBottom: 10
    },
    children: section.sub_header
  }), /*#__PURE__*/_jsx(ItemList, {
    items: section.list_items,
    tokens: tokens
  }), section.video_tutorial_links?.length > 0 && /*#__PURE__*/_jsx("ul", {
    style: {
      listStyle: 'none',
      margin: '10px 0 0',
      padding: 0
    },
    children: section.video_tutorial_links.map((link, index) => /*#__PURE__*/_jsx(VideoLink, {
      link: link,
      tokens: tokens
    }, index))
  })]
});
const Message = ({
  message,
  tokens: raw
}) => {
  const tokens = resolve(raw);
  if (message.loading) {
    return /*#__PURE__*/_jsx(Dots, {
      label: 'GRACI is typing',
      tokens: tokens
    });
  }
  if (message.error) {
    return /*#__PURE__*/_jsx("p", {
      role: 'alert',
      style: {
        ...body(tokens),
        color: tokens.danger,
        fontSize: 14
      },
      children: message.error
    });
  }
  if (message.role === 'user') {
    return /*#__PURE__*/_jsx("div", {
      style: {
        alignSelf: 'flex-end',
        backgroundColor: tokens.subdued,
        borderRadius: 18,
        borderBottomRightRadius: 6,
        maxWidth: '84%',
        padding: '11px 15px'
      },
      children: /*#__PURE__*/_jsx("p", {
        style: body(tokens),
        children: contentOf(message).message
      })
    });
  }
  let content = contentOf(message);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      alignSelf: 'stretch',
      maxWidth: '100%'
    },
    children: [content.message && /*#__PURE__*/_jsx("p", {
      style: body(tokens),
      children: content.message
    }), content.sections.map((section, index) => /*#__PURE__*/_jsx(Section, {
      section: section,
      tokens: tokens
    }, index)), content.request && /*#__PURE__*/_jsx("p", {
      style: {
        ...body(tokens),
        marginTop: content.message || content.sections.length ? 12 : 0
      },
      children: content.request
    })]
  });
};
export default Message;